/**
 * Locale store — the single source of truth for the active UI language.
 *
 * Built on zustand (same pattern as `stores/ui-store.ts`) so any component can
 * subscribe and re-render on a language change. The choice is persisted to
 * localStorage under `psn-locale` (the raw locale string — backward compatible
 * with the previous ad-hoc Profile selector), and every change applies the two
 * document-level side effects the rest of the app relies on:
 *
 *   • `document.documentElement.lang = <locale>`  (a11y / spellcheck / SEO)
 *   • `document.documentElement.dir  = 'rtl' | 'ltr'`  (RTL mirroring for `ar`)
 *
 * `i18n.changeLanguage(locale)` is exposed as a familiar, framework-style handle
 * (mirrors react-i18next's API) that simply drives this store.
 */

import { create } from 'zustand';

import { isLocaleLoaded, loadLocale } from './locales';
import {
  DEFAULT_LOCALE,
  LOCALE_SOURCE_KEY,
  LOCALE_STORAGE_KEY,
  type Locale,
  type LocaleSource,
  browserLanguages,
  detectLocale,
  dirFor,
  isLocale,
} from './config';

/** The page's localStorage (read through `window` so non-browser globals never shadow it). */
function ls(): Storage {
  return window.localStorage;
}

/**
 * Initial locale: the stored choice when there is one; otherwise (first visit)
 * auto-detect from the browser's Accept-Language list and remember it as an
 * `auto` choice so the detection is stable across reloads.
 */
function readInitialLocale(): Locale {
  try {
    const raw = ls().getItem(LOCALE_STORAGE_KEY);
    if (isLocale(raw)) return raw;
  } catch {
    /* private mode / disabled storage → detect below */
  }
  const detected = detectLocale(browserLanguages());
  persistLocale(detected, 'auto');
  return detected;
}

/** How the stored locale was chosen (`auto` when unknown / legacy). */
export function readLocaleSource(): LocaleSource {
  try {
    return ls().getItem(LOCALE_SOURCE_KEY) === 'user' ? 'user' : 'auto';
  } catch {
    return 'auto';
  }
}

/** Apply `lang` + `dir` to <html>. No-op outside the browser (SSR-safe). */
export function applyLocaleToDocument(locale: Locale): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.setAttribute('lang', locale);
  root.setAttribute('dir', dirFor(locale));
}

/** Persist the raw locale string (+ how it was chosen); ignore storage failures. */
export function persistLocale(locale: Locale, source: LocaleSource = 'user'): void {
  try {
    ls().setItem(LOCALE_STORAGE_KEY, locale);
    ls().setItem(LOCALE_SOURCE_KEY, source);
  } catch {
    /* ignore */
  }
}

interface LocaleState {
  locale: Locale;
  /**
   * The locale whose catalog is being fetched for a switch (non-default locales
   * are lazy chunks), or null. Language switchers show a busy state meanwhile.
   */
  loadingLocale: Locale | null;
  /**
   * Switch language (fire-and-forget; see `changeLocale` to await it). Applies
   * synchronously when the catalog is already in memory.
   */
  setLocale: (locale: Locale) => void;
}

export const useLocaleStore = create<LocaleState>(() => ({
  locale: readInitialLocale(),
  loadingLocale: null,
  setLocale: (locale) => {
    void changeLocale(locale);
  },
}));

function applySwitch(locale: Locale): void {
  applyLocaleToDocument(locale);
  useLocaleStore.setState({ locale, loadingLocale: null });
}

/**
 * Switch language: persists the choice immediately, fetches the catalog when it
 * is not loaded yet (non-default locales are lazy chunks), then updates state
 * and flips <html> lang + dir — so the UI never renders a locale whose copy is
 * not in memory. When the catalog is already loaded the switch is applied
 * SYNCHRONOUSLY (before this returns). Resolves once the switch is applied, or
 * abandoned because the catalog could not load / a newer switch superseded it.
 */
export function changeLocale(locale: Locale): Promise<void> {
  const state = useLocaleStore.getState();
  if (!isLocale(locale) || locale === state.locale) {
    // Still re-apply document attributes for the initial/identical case so a
    // fresh reload always reflects the persisted choice.
    if (isLocale(locale)) applyLocaleToDocument(locale);
    if (isLocale(locale) && state.loadingLocale !== null) useLocaleStore.setState({ loadingLocale: null });
    return Promise.resolve();
  }
  persistLocale(locale, 'user');
  if (isLocaleLoaded(locale)) {
    applySwitch(locale);
    return Promise.resolve();
  }
  useLocaleStore.setState({ loadingLocale: locale });
  return loadLocale(locale).then(
    () => {
      // A newer switch superseded this one while the chunk was in flight.
      if (useLocaleStore.getState().loadingLocale === locale) applySwitch(locale);
    },
    () => {
      // Chunk unreachable (offline / stale deploy): keep the current language
      // on screen; the persisted choice is retried on the next page load.
      if (useLocaleStore.getState().loadingLocale === locale) useLocaleStore.setState({ loadingLocale: null });
    },
  );
}

/**
 * Make sure the ACTIVE locale's catalog is in memory before the app renders
 * (called by the bootstrap before it imports the application modules, so
 * module-level `tr()` copy is evaluated in the right language too). If the
 * chunk cannot be fetched the session degrades honestly to the bundled English
 * catalog — the stored preference is kept and retried on the next load.
 */
export async function ensureActiveLocaleLoaded(): Promise<Locale> {
  const { locale } = useLocaleStore.getState();
  if (!isLocaleLoaded(locale)) {
    try {
      await loadLocale(locale);
    } catch {
      useLocaleStore.setState({ locale: DEFAULT_LOCALE, loadingLocale: null });
    }
  }
  const active = useLocaleStore.getState().locale;
  applyLocaleToDocument(active);
  return active;
}

// Apply the persisted locale to <html> as soon as this module loads, so the
// very first paint already has the correct lang + direction (mirrors the way
// ui-store applies the persisted theme on load).
applyLocaleToDocument(useLocaleStore.getState().locale);

/**
 * Framework-style handle. `changeLanguage` is the canonical mutation the app
 * (and the Profile selector) calls; `language` / `dir` are convenience reads for
 * non-React callers.
 */
export const i18n = {
  get language(): Locale {
    return useLocaleStore.getState().locale;
  },
  changeLanguage(locale: Locale): Promise<void> {
    return changeLocale(locale);
  },
  dir(locale: Locale = useLocaleStore.getState().locale): 'ltr' | 'rtl' {
    return dirFor(locale);
  },
};
